'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/lib/auth-context';
import { authFetch } from '@/lib/authFetch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  getRecentLogs,
  getRecentBackendErrors,
  getLastRequestId,
  getLastErrorCode,
  getCommitSha,
} from '@/lib/client-logger';

const API_URL = process.env.NEXT_PUBLIC_API_URL || '';
const LOG_BUFFER_SIZE = 20;
const NET_BUFFER_SIZE = 30;
const CLICK_BUFFER_SIZE = 10;

type LogEntry = { level: string; ts: string; message: string };
type NetEntry = {
  url: string;
  method: string;
  status: number;
  duration_ms: number;
  ts: string;
  error?: string;
};
type ClickEntry = {
  ts: string;
  tag: string;
  text: string;
  path: string;
  selector: string;
};

// Module-level ring buffers. Populated by install*Capture() on first mount;
// persist across route changes so the operator can open the bug modal and
// capture events that fired BEFORE they clicked "Report a bug".
let _logBuffer: LogEntry[] = [];
let _netBuffer: NetEntry[] = [];
let _clickBuffer: ClickEntry[] = [];
let _consoleInstalled = false;
let _netInstalled = false;
let _clickInstalled = false;

function installConsoleCapture() {
  if (_consoleInstalled || typeof window === 'undefined') return;
  _consoleInstalled = true;

  const levels = ['log', 'warn', 'error', 'info'] as const;
  for (const level of levels) {
    const original = console[level].bind(console);
    (console as unknown as Record<string, (...args: unknown[]) => void>)[level] = (...args: unknown[]) => {
      try {
        const parts = args.map((a) => {
          if (a instanceof Error) return `${a.name}: ${a.message}`;
          if (typeof a === 'string') return a;
          try { return JSON.stringify(a); } catch { return String(a); }
        });
        _logBuffer.push({
          level,
          ts: new Date().toISOString(),
          message: parts.join(' ').slice(0, 500),
        });
        if (_logBuffer.length > LOG_BUFFER_SIZE) {
          _logBuffer = _logBuffer.slice(-LOG_BUFFER_SIZE);
        }
      } catch { /* capture must never break the app */ }
      original(...args);
    };
  }

  window.addEventListener('error', (ev) => {
    _logBuffer.push({
      level: 'error',
      ts: new Date().toISOString(),
      message: `[unhandled] ${ev.message} @ ${ev.filename}:${ev.lineno}`.slice(0, 500),
    });
    if (_logBuffer.length > LOG_BUFFER_SIZE) _logBuffer = _logBuffer.slice(-LOG_BUFFER_SIZE);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    _logBuffer.push({
      level: 'error',
      ts: new Date().toISOString(),
      message: `[unhandledrejection] ${String(ev.reason)}`.slice(0, 500),
    });
    if (_logBuffer.length > LOG_BUFFER_SIZE) _logBuffer = _logBuffer.slice(-LOG_BUFFER_SIZE);
  });
}

// Patches window.fetch to record URL/method/status/duration for every request.
// Skips the bug-reports POST itself so we don't recursively log submissions.
function installNetworkCapture() {
  if (_netInstalled || typeof window === 'undefined') return;
  _netInstalled = true;
  const original = window.fetch.bind(window);
  window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const started = Date.now();
    let urlStr = '';
    let method = 'GET';
    try {
      if (typeof input === 'string') urlStr = input;
      else if (input instanceof URL) urlStr = input.toString();
      else urlStr = input.url;
      method = (init?.method || (input instanceof Request ? input.method : 'GET') || 'GET').toUpperCase();
    } catch { /* noop */ }

    const skip = urlStr.includes('/api/bug-reports');
    let status = 0;
    let errMsg: string | undefined;
    try {
      const res = await original(input, init);
      status = res.status;
      return res;
    } catch (e) {
      errMsg = e instanceof Error ? e.message : String(e);
      throw e;
    } finally {
      if (!skip) {
        _netBuffer.push({
          url: urlStr.slice(0, 300),
          method,
          status,
          duration_ms: Date.now() - started,
          ts: new Date(started).toISOString(),
          error: errMsg,
        });
        if (_netBuffer.length > NET_BUFFER_SIZE) _netBuffer = _netBuffer.slice(-NET_BUFFER_SIZE);
      }
    }
  };
}

// Captures every click globally. Records element tag, inner text (first 80
// chars), element path, and a short CSS selector. Skips clicks inside the
// bug-report modal itself so the user opening the modal isn't the last
// "click" recorded. Ring buffer of the last 10 clicks.
function installClickCapture() {
  if (_clickInstalled || typeof window === 'undefined') return;
  _clickInstalled = true;

  function elementPath(el: Element): string {
    const parts: string[] = [];
    let cur: Element | null = el;
    for (let i = 0; cur && i < 6; i++) {
      const part = cur.tagName.toLowerCase();
      const id = cur.id ? `#${cur.id}` : '';
      const cls = typeof cur.className === 'string' && cur.className
        ? '.' + cur.className.trim().split(/\s+/).slice(0, 2).join('.')
        : '';
      parts.unshift(part + id + cls);
      cur = cur.parentElement;
    }
    return parts.join(' > ');
  }

  document.addEventListener('click', (ev) => {
    try {
      const target = ev.target as Element | null;
      if (!target) return;
      // Skip clicks inside the bug-report modal itself.
      if (target.closest('[data-bug-modal]')) return;
      // Skip clicks on the bug-report button.
      if (target.closest('[data-bug-button]')) return;

      const tag = target.tagName.toLowerCase();
      const text = (target.textContent || '').trim().slice(0, 80);
      const path = elementPath(target);
      const selector = target.id
        ? `#${target.id}`
        : tag + (target.getAttribute('data-testid') ? `[data-testid="${target.getAttribute('data-testid')}"]` : '');

      _clickBuffer.push({
        ts: new Date().toISOString(),
        tag,
        text,
        path: path.slice(0, 300),
        selector: selector.slice(0, 120),
      });
      if (_clickBuffer.length > CLICK_BUFFER_SIZE) {
        _clickBuffer = _clickBuffer.slice(-CLICK_BUFFER_SIZE);
      }
    } catch { /* capture must never break the app */ }
  }, true);
}

async function captureScreenshot(): Promise<string | null> {
  try {
    const mod = await import('html2canvas-pro');
    const html2canvas = (mod as unknown as { default: (el: HTMLElement, opts?: Record<string, unknown>) => Promise<HTMLCanvasElement> }).default;
    const canvas = await html2canvas(document.body, {
      backgroundColor: null,
      scale: Math.min(window.devicePixelRatio || 1, 1.5),
      logging: false,
      useCORS: true,
      // Clip captures to the visible viewport to keep file size sane.
      width: window.innerWidth,
      height: window.innerHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      x: window.scrollX,
      y: window.scrollY,
    });
    // JPEG at 0.65 quality gives a much smaller payload than PNG for
    // screenshots of rendered UI. Browsers cap dataURL length by design
    // but this is usually 200-600KB for a 1080p viewport.
    return canvas.toDataURL('image/jpeg', 0.65);
  } catch (e) {
    console.warn('[bug-report] screenshot capture failed:', e);
    return null;
  }
}

const GENRES = [
  { value: '', label: 'Which area? (optional)' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'agent', label: 'Agent / chat' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'workflow', label: 'Workflow builder' },
  { value: 'widget', label: 'Widget / chatbot' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'other', label: 'Other' },
];

type BugReportButtonProps = {
  // 'floating' — fixed bottom-left pill (default; for regular users on every page)
  // 'header'   — compact icon button intended to live inside HeaderActions
  //              next to PlanToggle. Only renders when the current viewer is
  //              actually viewing as admin (so non-admins keep the floating one).
  variant?: 'floating' | 'header';
};

export default function BugReportButton({ variant = 'floating' }: BugReportButtonProps = {}) {
  const { user, tenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [genre, setGenre] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    installConsoleCapture();
    installNetworkCapture();
    installClickCapture();
  }, []);

  // Only visible when signed in — don't want anonymous submissions.
  if (!user || !tenant) return null;

  // Single trigger lives in HeaderActions next to the bell for everyone (not
  // just admins). The legacy floating bottom-left mount in app/layout is now
  // a no-op — its variant short-circuits return so it doesn't render twice.
  if (variant === 'floating') return null;

  async function submit() {
    const trimmed = message.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);

    // Auto-capture the screen behind the modal. We hide the modal for one
    // frame so it isn't in the shot, then restore. If capture fails we
    // still submit — the report shouldn't block on screenshot failure.
    let screenshot: string | null = null;
    const modal = document.querySelector('[data-bug-modal]') as HTMLElement | null;
    const prevVisibility = modal?.style.visibility || '';
    if (modal) modal.style.visibility = 'hidden';
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    try {
      screenshot = await captureScreenshot();
    } catch { /* non-fatal */ }
    if (modal) modal.style.visibility = prevVisibility;

    // Phase B-1 observability bundle: pull from the global clientLogger
    // ring buffer so the report includes the same context any other
    // observability surface sees (request_id, error_code, commit_sha,
    // recent backend errors, last 50 logs).
    const lastRequestId = getLastRequestId();
    const lastErrorCode = getLastErrorCode();
    const recentLogs = getRecentLogs(50);
    const recentErrors = getRecentBackendErrors(10);
    let commitSha: string | null = null;
    try { commitSha = await getCommitSha(); } catch { /* non-fatal */ }

    try {
      const res = await authFetch(`${API_URL}/api/bug-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          genre: genre || null,
          page_url: window.location.href,
          user_agent: navigator.userAgent,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          console_logs: _logBuffer,
          network_logs: _netBuffer,
          click_trail: _clickBuffer,
          screenshot,
          // First-class observability fields (migration 070).
          request_id: lastRequestId || undefined,
          error_code: lastErrorCode || undefined,
          commit_sha: commitSha || undefined,
          // Augment console_logs with the structured logs we collected
          // via clientLogger.recordLog (different ring buffer to the
          // legacy console-patch one above). Both shapes share { level,
          // ts, message } so they merge cleanly.
          recent_errors: recentErrors,
          // Pass-through; older servers ignore unknown fields.
          structured_logs: recentLogs,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || body?.error || `HTTP ${res.status}`);
      }
      setSubmitted(true);
      setMessage('');
      setGenre('');
      setTimeout(() => { setOpen(false); setSubmitted(false); }, 1800);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setSubmitting(false);
    }
  }

  const bugIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 2l1.88 1.88" />
      <path d="M14.12 3.88L16 2" />
      <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
      <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6z" />
      <path d="M12 20v-9" />
      <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
      <path d="M6 13H2" />
      <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
      <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
      <path d="M22 13h-4" />
      <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
    </svg>
  );

  // The 'floating' variant short-circuits above with `return null`, so this
  // render path is always the header-icon trigger. Kept the variant prop
  // (default 'floating') so the legacy mount in app/layout.tsx stays a
  // harmless no-op without me having to coordinate three file edits.
  return (
    <>
      <button
        data-bug-button
        onClick={() => setOpen(true)}
        aria-label="Report a bug"
        title="Report a bug"
        className="p-2 rounded-lg hover:bg-[--bg-main] text-[--text-secondary] hover:text-[--text-primary] transition-colors"
      >
        {bugIcon}
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="bug-report-title"
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-4 sm:items-center"
          onClick={() => !submitting && setOpen(false)}
        >
          <div
            data-bug-modal
            className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl dark:bg-[#141414]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="bug-report-title" className="mb-1 text-lg font-semibold">Report a bug</h2>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Tell us what you were doing and what went wrong. We&apos;ll automatically include the page URL, recent browser logs, and recent API calls.
            </p>

            {submitted ? (
              <div className="rounded-lg bg-[#00C2A8]/10 p-4 text-center text-sm text-[#00a68f]">
                Bug report sent. We&apos;ll take a look.
              </div>
            ) : (
              <>
                <Select
                  value={genre || undefined}
                  onValueChange={(v) => setGenre(v)}
                  disabled={submitting}
                >
                  <SelectTrigger className="mb-2 w-full">
                    <SelectValue placeholder="Which area? (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENRES.filter(g => g.value).map((g) => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 5000))}
                  placeholder="What happened? What did you expect?"
                  rows={5}
                  autoFocus
                  disabled={submitting}
                  className="w-full rounded-lg border border-gray-200 bg-transparent p-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#00C2A8] focus:outline-none focus:ring-1 focus:ring-[#00C2A8]/30 dark:border-gray-700 dark:text-gray-100 dark:placeholder:text-gray-500"
                />
                <div className="mt-1 flex items-center justify-between text-[11px] text-gray-400">
                  <span>We&apos;ll capture a screenshot + recent clicks/logs/API calls automatically.</span>
                  <span>{message.length}/5000</span>
                </div>

                {error && (
                  <div className="mt-2 rounded-lg bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
                    {error}
                  </div>
                )}
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    onClick={() => setOpen(false)}
                    disabled={submitting}
                    className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submit}
                    disabled={submitting || !message.trim()}
                    className="rounded-lg bg-[#00C2A8] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#00a68f] disabled:opacity-50"
                  >
                    {submitting ? 'Sending…' : 'Send report'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
